import React, { useEffect, useState, useRef, useLayoutEffect, useMemo } from "react";
import {
    api, getZone, getRowsByZoneId, getSeatsOfZone, getSeatMapShapes, getSeatStatuses, getSeatOccupanciesOfEvent
} from "ticketino-api-client";
import {
    SEAT_RADIUS,
    SEAT_TEMPLATE_EXPANDED_WIDTH,
    SEAT_TEMPLATE_EXPANDED_HEIGHT,
    SEAT_TEMPLATE_EXPANDED_TEXT_MAXWIDTH,
    getSeatsBounds,
    getSeatTitle,
    transformSeats,
    translateSeats,
    SeatStatuses,
    getSeatStatusColor,
    getExpandedSeatTitle,
    getRectCoords,
} from "./seatMapSeatsUtils"
import {
    getId,
    idEq,
    idIn,
    idNotIn,
    last,
    objectMap,
    propNotEq,
} from "./functionalUtils"
import axios from 'axios';

import Loading from "../Loading";

const Zone = (props) => {

    //----variables----

    //developement env dependent
    const baseUrl = process.env.REACT_APP_BASE_TICKETINO_URL;
    const baseProxyUrl = process.env.REACT_APP_PLUGIN_PROXY;

    //prop values
    const selectedEventId = props.eventId;
    const selectedZoneId = props.zoneId;
    const selectedLanguage = props.languageISO;
    const selectedSeatIds = props.selectedSeatIds;
    const onSeatClick = props.onSeatClick;
    const ticketTypes = props.ticketTypes;

    const [loading, setLoading] = useState(true);
    const [resources, setResources] = useState([]);
    const [selectedZone, setSelectedZone] = useState({});
    const [rows, setRows] = useState([]);
    const [seats, setSeats] = useState([]);
    const [seatmapShapes, setSeatmapShapes] = useState([]);
    const [seatStatuses, setSeatStatuses] = useState([]);
    const [mouseDownPos, setMouseDownPos] = useState(null)
    const [mouseMovePos, setMouseMovePos] = useState(null)
    const [requestSeatOccupancy, setRequestSeatOccupancy] = useState(false)

    const [zoneScale, setZoneScale] = useState(1)
    const [zoneX, setZoneX] = useState(0)
    const [zoneY, setZoneY] = useState(0)
    const zoneRef = useRef(null)

    const [showMobileBasket, setShowMobileBasket] = useState(false);

    const [token] = useState(localStorage.getItem("token"));

    useEffect(() => {
        requestResources(selectedLanguage);
    }, [selectedLanguage]);

    useEffect(() => {
        axios.defaults.headers.common["Authorization"] = "Bearer " + token;
        api.defaults.headers.common['Authorization'] = "Bearer " + token;

    }, []);

    useEffect(() => {
        setRequestSeatOccupancy(false);
        requestSelectedZone();
    }, [selectedZoneId]);


    useEffect(() => {

        if (requestSeatOccupancy === false) return;

        const interval = setInterval(async () => {
            try {
                updateSeatOccupancies(seats);
            } catch (error) {
            }
        }, 5000);

        return () => clearInterval(interval); // Cleanup on unmount
    }, [requestSeatOccupancy]);

    useEffect(() => {
        const { current } = zoneRef;

        if (!current) {
            return;
        }
        //prevent scroll when zooming in/out
        current.addEventListener('wheel', (e) => { e.preventDefault(); }, { passive: false });

    }, [selectedZone]);

    useEffect(() => {
        requestZoneDetails(selectedLanguage);
    }, [selectedZone]);

    const seatStatusValues = {
        EMPTY: 1,
        RESERVED: 2,
        SOLD: 3,
        RESERVED_BY_ORGANIZER: 4,
        OCCUPIED: 5,
        TABLE: 6,
        DONT_SHOW: 7,
        PLACEHOLDER: 8,
        RESERVED_BY_ORGANIZER_ME: 9,
        RESERVED_ME: 11,
    }

    const selectedSeats = useMemo(() => {
        const result = selectedSeatIds.map(id => seats.find(idEq(id)));
        return result;
    }, [selectedSeatIds, seats]);

    const unselectedSeats = useMemo(() => {
        const result = seats.filter(idNotIn(selectedSeatIds));
        return result;
    }, [selectedSeatIds, seats]);

    //request resources
    const requestSelectedZone = async (langauge) => {

        if (selectedZoneId > 0) {
            var zone = await getZone(selectedZoneId);
            setSelectedZone(zone);
        }
    };

    //request resources
    const requestResources = async (langauge) => {
        await axios
            .get(`form/resources/${selectedLanguage}`)
            .then((res) => {
                setResources(res.data?.seatmap);
            })
            .catch((error) => console.error(error.response.data));
    };

    const requestZoneDetails = async (selectedLanguage) => {

        var seatmapShapes = await getSeatMapShapes();
        setSeatmapShapes(seatmapShapes);

        var seatStatuses = await getSeatStatuses(selectedLanguage);
        setSeatStatuses(seatStatuses);

        if (selectedZone && Object.keys(selectedZone).length > 0 && selectedZone.id > 0) {

            var rows = await getRowsByZoneId(selectedZone.id);
            setRows(rows);

            var seatsOfEvent = await getSeatsOfZone(selectedZone.id, selectedEventId);
            setSeats(seatsOfEvent);

            setRequestSeatOccupancy(true);

            resetZoneXYScale(seatsOfEvent, selectedZone);
        }
    }


    const updateSeatOccupancies = async (currentSeats) => {

        var seatOccupancies = await getSeatOccupanciesOfEvent(selectedEventId);

        if (!(currentSeats.length > 0)) return;

        const updatedSeats = currentSeats.map(seat => {
            const seatOccupancy = seatOccupancies.find(so => so.seatId === seat.id);

            return {
                ...seat,
                seatOccupancy: seatOccupancy ?? seat.seatOccupancy,
            };
        });

        setSeats(updatedSeats);
    };

    const resetZoneXYScale = (seats, zone) => {
        if (seats.length === 0) {
            setZoneX(0)
            setZoneY(0)
            setZoneScale(1)
            return
        }

        const stageCoords = zone?.stage.width ? getRectCoords(zone.stage) : []
        const backgroundImageCoords = zone?.backgroundImage?.url
            ? getRectCoords(zone.backgroundImage)
            : []
        const seatsBounds = getSeatsBounds(
            [...stageCoords, ...backgroundImageCoords, ...seats],
            SEAT_RADIUS + 10,
        )

        const seatsWidth = seatsBounds.right - seatsBounds.left
        const seatsHeight = seatsBounds.bottom - seatsBounds.top

        const zoneBounds = zoneRef.current.getBoundingClientRect()
        const zoneWidth = zoneBounds.right - zoneBounds.left
        const zoneHeight = zoneBounds.bottom - zoneBounds.top

        const scale = Math.min(zoneWidth / seatsWidth, zoneHeight / seatsHeight)

        setZoneX(-seatsBounds.left - seatsWidth / 2 + zoneWidth / 2 / scale)
        setZoneY(-seatsBounds.top - seatsHeight / 2 + zoneHeight / 2 / scale)
        setZoneScale(scale)
    }

    //const PieChart = ({ colors, r, ...props }) => {
    //    r /= 2
    //    const circumference = 2 * Math.PI * r
    //    const toCircUnit = p => (p / 100) * circumference
    //    const percent = 100 / colors.length

    //    return colors.map((color, i) => (
    //        <circle
    //            {...props}
    //            r={r}
    //            fill="transparent"
    //            stroke={color}
    //            strokeWidth={r * 2}
    //            strokeDasharray={`${toCircUnit(percent)} ${toCircUnit(
    //                100 - percent,
    //            )}`}
    //            strokeDashoffset={toCircUnit(25 + (100 - percent * i))}
    //        />
    //    ))
    //}

    const PieChart = ({ color, r, ...props }) => {
        r /= 2;
        return (
            <circle
                {...props}
                r={r}
                fill="transparent"
                stroke={color}
                strokeWidth={r * 2} // Make the stroke take up the full ring
            />
        );
    }



    const toZoneCoords = (clientX, clientY) => {
        const zoneBounds = zoneRef.current.getBoundingClientRect()
        return [
            (clientX - zoneBounds.left) / zoneScale - zoneX,
            (clientY - zoneBounds.top) / zoneScale - zoneY,
        ]
    }

    const handleMouseUp = event => {

        setMouseDownPos(null)
        setMouseMovePos(null)

        if (!mouseMovePos || !mouseDownPos) return
    }


    const scaleSeats = (scaleIn = false, x, y) => {
        const zoneBounds = zoneRef.current.getBoundingClientRect()
        x = x ?? (zoneBounds.left + zoneBounds.right) / 2
        y = y ?? (zoneBounds.top + zoneBounds.bottom) / 2
        const scalor = 1.1
        const newZoneScale = scaleIn ? zoneScale * scalor : zoneScale / scalor
        const [x1, y1] = toZoneCoords(x, y)
        const x2 = (x - zoneBounds.left) / newZoneScale
        const y2 = (y - zoneBounds.top) / newZoneScale

        setZoneScale(newZoneScale)
        setZoneX(x2 - x1)
        setZoneY(y2 - y1)
    }

    const mapSeat =
        (isSelected) =>
            ({ id, rowId, name, x, y, status, seatOccupancy, ticketTypeIds = [] }) => {

                let seatTicketTypes = [];

                ticketTypeIds.forEach(id => {
                    const ticketType = ticketTypes.find(tt => tt.id === id);
                    if (ticketType) {
                        seatTicketTypes.push(ticketType);
                    }
                });

                seatTicketTypes = seatTicketTypes.sort((a, b) => a.sortOrder - b.sortOrder);

                var seatStatus = seatOccupancy?.seatStatus ?? status;

                const seatStyle = {
                    fill: isSelected
                        ? "#ee7324"
                        : status === seatStatusValues.TABLE
                            ? "black"
                            : (seatStatus === 2
                                ? "url(#stripedPattern)"
                                : getSeatStatusColor(seatStatus))
                }

                const seatColor = seatTicketTypes?.slice()?.sort((a, b) => a.sortOrder - b.sortOrder)[0]?.colorCode || null;


                var seat = <React.Fragment key={id}>
                    <circle
                        cx={x}
                        cy={y}
                        r={SEAT_RADIUS}
                        style={{
                            cursor: seatStatus === seatStatusValues.EMPTY && seatTicketTypes.length
                                ? "pointer"
                                : "not-allowed"
                        }}
                        onMouseDown={() => {
                            //if (isSelected) setIsDraggingSelection(true)
                        }}
                        onMouseUp={event => {
                            if (seatStatus === seatStatusValues.EMPTY && seatTicketTypes.length) {
                                onSeatClick?.(event, {
                                    id,
                                    zoneName: selectedZone.zoneName,
                                    rowId,
                                    seatName: name,
                                    rowName: rows.find(r => r.id === rowId)?.name,
                                    status,
                                    seatTicketTypes
                                }, isSelected);
                            }
                        }}
                        {...(seatStyle)}
                    >
                        <title>
                            {getSeatTitle({
                                rowNameText: resources?.Row,
                                seatText: resources?.Seat,
                                statusText: resources?.Status,
                                ticketTypesText: resources?.TicketTypes,
                                seatStatuses,
                                rowId,
                                rows,
                                status: seatStatus,
                                name,
                                ticketTypes: seatTicketTypes,
                            })}
                        </title>
                    </circle>

                    {seatTicketTypes.length && seatStatus === seatStatusValues.EMPTY && (
                        <PieChart
                            color={seatColor}
                            cx={x}
                            cy={y}
                            r={SEAT_RADIUS * 0.6}
                            style={{ pointerEvents: "none" }}
                        />
                    )}
                </React.Fragment>


                return seat
            }

    const selectedSeatElements = useMemo(() => {
        if (!Array.isArray(selectedSeats)) return [];
        return selectedSeats.map(mapSeat(true))
    }, [selectedSeats, resources])

    const unselectedSeatElements = useMemo(() => {
        if (!Array.isArray(unselectedSeats)) return [];
        return unselectedSeats.map(mapSeat(false));
    }, [unselectedSeats, resources]);


    return (
        <div>
            {!loading ? <Loading /> :
                <div className="seatmap-zone" style={{ position: "relative", border: "1px solid #bbb" }}>
                    {selectedZone && Object.keys(selectedZone).length > 0 && selectedZone.id > 0 &&
                        <div
                            id="zone"
                            className="flex-grow-1 row m-0 h-100"
                            ref={zoneRef}
                            style={{
                                overflow: "hidden",
                                cursor: "pointer",
                            }}
                            onDragStart={e => e.preventDefault()}
                            onMouseDown={({ clientX, clientY }) => {
                                const [x, y] = toZoneCoords(clientX, clientY)
                                setMouseDownPos({ x, y })
                            }}
                            onMouseMove={({ clientX, clientY }) => {
                                if (mouseDownPos === null) return

                                const [x, y] = toZoneCoords(clientX, clientY)
                                setMouseMovePos({ x, y })

                                setZoneX(x + zoneX - mouseDownPos.x)
                                setZoneY(y + zoneY - mouseDownPos.y)
                            }}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                            onWheel={(event) => {
                                const { deltaY, clientX, clientY } = event;
                                scaleSeats(deltaY < 0, clientX, clientY);
                            }}
                            onTouchStart={(e) => {
                                if (e.touches.length === 1) {
                                    const touch = e.touches[0];
                                    const [x, y] = toZoneCoords(touch.clientX, touch.clientY);
                                    setMouseDownPos({ x, y, pinchDist: null });
                                } else if (e.touches.length === 2) {
                                    const [t1, t2] = e.touches;
                                    const center = [(t1.clientX + t2.clientX) / 2, (t1.clientY + t2.clientY) / 2];
                                    const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
                                    setMouseDownPos({ pinchDist: dist, center, startX: zoneX, startY: zoneY, startScale: zoneScale });
                                }
                            }}
                            onTouchMove={(e) => {
                                e.preventDefault(); // prevent scrolling
                                if (e.touches.length === 1 && mouseDownPos && !mouseDownPos.pinchDist) {
                                    // single touch → drag
                                    const touch = e.touches[0];
                                    const [x, y] = toZoneCoords(touch.clientX, touch.clientY);
                                    setZoneX(zoneX + x - mouseDownPos.x);
                                    setZoneY(zoneY + y - mouseDownPos.y);
                                    setMouseMovePos({ x, y });
                                } else if (e.touches.length === 2 && mouseDownPos && mouseDownPos.pinchDist) {
                                    // pinch → zoom
                                    const [t1, t2] = e.touches;
                                    const newDist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);

                                    const scaleFactor = newDist / mouseDownPos.pinchDist;
                                    const [cx, cy] = mouseDownPos.center;

                                    // calculate new scale
                                    const newZoneScale = zoneScale * scaleFactor;

                                    // adjust X/Y to zoom around center
                                    const [zoneX0, zoneY0] = toZoneCoords(cx, cy);
                                    const zoneX1 = (cx - zoneRef.current.getBoundingClientRect().left) / newZoneScale - zoneX0;
                                    const zoneY1 = (cy - zoneRef.current.getBoundingClientRect().top) / newZoneScale - zoneY0;

                                    setZoneScale(newZoneScale);
                                    setZoneX(zoneX1);
                                    setZoneY(zoneY1);

                                    // update pinch distance for next move
                                    setMouseDownPos({ ...mouseDownPos, pinchDist: newDist });
                                }
                            }}
                        >
                            <svg
                                id="seatmapSvg"
                                width="100%"
                                height="100%"
                                xmlns="http://www.w3.org/2000/svg"
                                xmlnsXlink="http://www.w3.org/1999/xlink">
                                <defs>
                                    <pattern id="stripedPattern" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
                                        <rect width="3" height="6" fill="#ccc" />
                                    </pattern>
                                </defs>
                                <g transform={`translate(${zoneX * zoneScale} ${zoneY * zoneScale}) scale(${zoneScale} ${zoneScale})`}>
                                    {selectedZone?.backgroundImage.url && (
                                        <image
                                            x={selectedZone.backgroundImage.x - SEAT_RADIUS}
                                            y={selectedZone.backgroundImage.y - SEAT_RADIUS}
                                            width={selectedZone.backgroundImage.width}
                                            height={selectedZone.backgroundImage.height}
                                            href={selectedZone.backgroundImage.url}
                                            style={{ pointerEvents: "none" }}
                                        />
                                    )}

                                    {selectedZone?.stage.width && (
                                        <>
                                            <rect
                                                x={selectedZone.stage.x - SEAT_RADIUS}
                                                y={selectedZone.stage.y - SEAT_RADIUS}
                                                width={selectedZone.stage.width}
                                                height={selectedZone.stage.height}
                                                fill="#939da6"
                                            />
                                            <text
                                                x={
                                                    selectedZone.stage.x +
                                                    selectedZone.stage.width / 2 -
                                                    SEAT_RADIUS
                                                }
                                                y={
                                                    selectedZone.stage.y +
                                                    selectedZone.stage.height / 2 -
                                                    SEAT_RADIUS
                                                }
                                                dominantBaseline="middle"
                                                textAnchor="middle"
                                                fill="white"
                                                fontWeight="bold"
                                                style={{ userSelect: "none" }}
                                            >
                                                {selectedZone.stage.stageTitle}
                                            </text>
                                        </>
                                    )}
                                    {unselectedSeatElements}

                                    {selectedSeatElements}
                                </g>
                            </svg>
                            <div className="seatmap-controls">
                                {/* Move Controls */}
                                <div className="move-controls">
                                    <div className="move-controls-grid">
                                        <div></div>
                                        <button onClick={() => setZoneY(zoneY + 100)}>↑</button>
                                        <div></div>

                                        <button onClick={() => setZoneX(zoneX - 100)}>←</button>
                                        <div style={{ visibility: 'hidden' }}></div>
                                        <button onClick={() => setZoneX(zoneX + 100)}>→</button>

                                        <div></div>
                                        <button onClick={() => setZoneY(zoneY - 100)}>↓</button>
                                        <div></div>
                                    </div>
                                </div>

                                {/* Zoom Controls */}
                                <div className="zoom-controls">
                                    <button onClick={() => scaleSeats(false)}>-</button>
                                    <button onClick={() => scaleSeats(true)}>+</button>
                                </div>
                            </div>
                        </div>
                    }
                </div>
            }
        </div >
    )

}

export default Zone;